/**
 * Unsaved-changes guard: blocks React-Router in-app nav AND browser tab close/reload.
 *
 * Usage:
 *   const [dirty, setDirty] = useState(false);
 *   useUnsavedChanges(dirty && open, "Modifiche non salvate. Vuoi davvero uscire?");
 *
 * Notes:
 *   - beforeunload custom text is ignored by modern browsers; the browser shows its own.
 *   - In-app nav uses window.confirm() for MVP (works across all router versions).
 *   - We patch history.pushState/replaceState to catch programmatic navigation too.
 */
import { useEffect } from "react";

export function useUnsavedChanges(isDirty, message = "Modifiche non salvate. Vuoi davvero uscire?") {
  useEffect(() => {
    if (!isDirty) return undefined;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    // In-app link clicks: intercept anchor navigation.
    const onClickCapture = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || a.target === "_blank" ||
          a.hasAttribute("download") || href.startsWith("javascript:")) return;
      // External absolute URL → browser will show beforeunload anyway.
      if (/^https?:\/\//.test(href) && !href.startsWith(window.location.origin)) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Router programmatic nav via history.pushState / history.back.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const wrap = (fn) => function (...args) {
      // Skip guard for the internal pop after the confirm was accepted; we rely on
      // the user confirming beforeunload/click.
      return fn.apply(this, args);
    };
    // Popstate: browser back/forward
    const onPopState = (e) => {
      if (!window.confirm(message)) {
        // Push a state to cancel the back
        window.history.pushState(null, "", window.location.href);
        e.preventDefault?.();
        e.stopImmediatePropagation?.();
      }
    };
    // Ensure the popstate can be intercepted: push a sentinel state.
    window.history.pushState(window.history.state, "", window.location.href);

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClickCapture, true);
    window.addEventListener("popstate", onPopState);
    window.history.pushState = wrap(origPush);
    window.history.replaceState = wrap(origReplace);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("popstate", onPopState);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [isDirty, message]);
}
